import {
    LineChart,
    Line,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Tooltip,
    ReferenceArea,
    ReferenceLine,
    CartesianGrid,
} from "recharts";
import { fmtDate } from "../lib/api";

function CustomDot(props) {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    if (payload?.out_of_range) {
        return <circle cx={cx} cy={cy} r={5} fill="#C0392B" stroke="#fff" strokeWidth={1.5} />;
    }
    if (payload?.warning) {
        return <circle cx={cx} cy={cy} r={4.5} fill="#D97706" stroke="#fff" strokeWidth={1.5} />;
    }
    return <circle cx={cx} cy={cy} r={3.5} fill="#2C4C3B" stroke="#fff" strokeWidth={1} />;
}

export default function FridgeChart({ frigo, readings, height = 224, showTooltip = true }) {
    if (!readings || !readings.length) {
        return (
            <p className="text-sm text-stone-500 italic py-6 text-center">
                Nessuna lettura registrata in questo periodo.
            </p>
        );
    }

    const data = readings.map((r) => ({
        ...r,
        label: `${r.data.slice(8, 10)}/${r.data.slice(5, 7)} ${r.slot === "mattina" ? "mat" : "pom"}`,
    }));

    const min = frigo.min_c;
    const max = frigo.max_c;
    const buf = frigo.buffer_c ?? 0;
    const ymin = Math.min(...readings.map((r) => r.valore), min - 1) - 0.5;
    const ymax = Math.max(...readings.map((r) => r.valore), max + 1) + 0.5;

    return (
        <div className="w-full" style={{ height }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
                    <CartesianGrid stroke="#E5E2D9" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                        dataKey="label"
                        tick={{ fill: "#5C5C5C", fontSize: 10 }}
                        tickLine={false}
                        axisLine={{ stroke: "#E5E2D9" }}
                    />
                    <YAxis
                        domain={[ymin, ymax]}
                        tick={{ fill: "#5C5C5C", fontSize: 10 }}
                        tickLine={false}
                        axisLine={{ stroke: "#E5E2D9" }}
                        width={36}
                        unit="°"
                    />
                    <ReferenceArea
                        y1={min}
                        y2={max}
                        fill="#4A7C59"
                        fillOpacity={0.08}
                        stroke="#4A7C59"
                        strokeOpacity={0.25}
                        ifOverflow="extendDomain"
                    />
                    {buf > 0 && (
                        <>
                            <ReferenceLine y={min + buf} stroke="#D97706" strokeDasharray="3 3" strokeOpacity={0.55} />
                            <ReferenceLine y={max - buf} stroke="#D97706" strokeDasharray="3 3" strokeOpacity={0.55} />
                        </>
                    )}
                    {showTooltip && (
                        <Tooltip
                            contentStyle={{
                                background: "#fff",
                                border: "1px solid #E5E2D9",
                                borderRadius: 8,
                                fontSize: 12,
                            }}
                            formatter={(v, _n, p) => [
                                `${v} °C${p.payload.out_of_range ? " ⚠ fuori soglia" : p.payload.warning ? " ⚠ vicino al limite" : ""}`,
                                "Temperatura",
                            ]}
                            labelFormatter={(l, items) => {
                                const it = items?.[0]?.payload;
                                return it ? `${fmtDate(it.data)} · ${it.slot}` : l;
                            }}
                        />
                    )}
                    <Line
                        type="monotone"
                        dataKey="valore"
                        stroke="#2C4C3B"
                        strokeWidth={2}
                        dot={<CustomDot />}
                        activeDot={{ r: 6 }}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}
